import { z } from 'zod';
import { CadDeclaredMaterialSchema } from './cadImport.js';
import type { OptomechMaterial, OptomechThermoOptic } from './optomech.js';

/**
 * Optomech materials and body bindings — spec
 * docs/superpowers/specs/2026-10-16-optomech-parts-library-design.md §4.1, §4.3.
 *
 * Units here are DATASHEET units (GPa, g/cm³, 10⁻⁶/K, °C), the ones a source
 * quotes and the ones the agent tool and the run form already take. The
 * conversion to the solver contract's deck units (MPa, t/mm³, 1/K) is
 * `toContractMaterial` below, the one function the backend's expansion step, the
 * agent tool and the run form all call.
 *
 * One datasheet fact is one type here, whatever carries it: a glass's index and
 * absolute dn/dT at one wavelength and temperature is `OptomechThermoOpticPoint`
 * on the datasheet answer, in a run's `materials_used` record and (paired from
 * the row's two entries) in `materialRowView`; a strength with its source is
 * `SourcedStrength`; where a number was read is `OptomechCitation`. The solver
 * wire (`OptomechThermoOptic`, the `tensile_strength_mpa`/`strength_source`
 * pair on `OptomechMaterial`) is a projection of these, made in one place each
 * (`thermoOpticForRun`, `strengthSourceText`).
 */

export const OPTOMECH_MATERIAL_KINDS = ['glass', 'metal', 'adhesive', 'ceramic', 'polymer', 'composite'] as const;
export const OptomechMaterialKindSchema = z.enum(OPTOMECH_MATERIAL_KINDS);
export type OptomechMaterialKind = z.infer<typeof OptomechMaterialKindSchema>;

export const OPTOMECH_MATERIAL_SCOPES = ['global', 'project'] as const;
export const OptomechMaterialScopeSchema = z.enum(OPTOMECH_MATERIAL_SCOPES);

/** The deck spelling of a material name: it becomes the CalculiX *MATERIAL name. */
export const OPTOMECH_DECK_NAME_RE = /^[A-Za-z0-9_-]{1,80}$/;

/**
 * A name's deck spelling: letters, digits, _ and - kept, everything else dropped
 * (N-BK7 stays; "S-FPL 51" → S-FPL51). The result can still be empty or too long;
 * a caller that writes it tests it against OPTOMECH_DECK_NAME_RE.
 */
export const deckNameOf = (name: string): string => name.replace(/[^A-Za-z0-9_-]/g, '');

/**
 * A number to 12 significant digits. A unit conversion by a power of ten is not
 * always exact (3.86 / 1e9 is 3.8599999999999995e-9, 17 digits), and
 * fea-service's SigFit deck writer refuses a number it cannot write exactly in 16
 * characters (the first assembly run on staging failed at export on exactly that
 * value, 2026-10-07). No datasheet carries 12 digits, so nothing real is lost.
 */
export const sig12 = (v: number): number => Number(v.toPrecision(12));

/** How close a stored wavelength must lie to the run's to be its point: 1 nm, within any catalog's rounding of a spectral line. */
export const OPTOMECH_WAVELENGTH_TOL_UM = 0.001;

// --- where a number was read ----------------------------------------------------

/**
 * Where one number was read: the document, the cell in it, its URL and the date
 * read. The parts catalog's citation and every material seed's entry source
 * (scripts/optomech-materials/seed.ts) and friction pair's source are this one
 * record. A material ROW's entries still carry it composed into a string
 * (`source` below) — the seed compiler's `citationText` — because the seed
 * fingerprint the global loader versions by covers the compiled entries.
 */
export const OptomechCitationSchema = z.object({
  document: z.string().min(1),
  locator: z.string().min(1),
  url: z.string().url().optional(),
  retrieved: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Required when the stored number is not the cell verbatim (an inch value converted to millimetres). */
  note: z.string().min(1).optional(),
}).strict();
export type OptomechCitation = z.infer<typeof OptomechCitationSchema>;

// --- the sourced points one datasheet fact is kept as ----------------------------

/**
 * The bound each property's value carries, the ones fea-service's
 * IsotropicMaterial enforces on the solver side (fea/model.py), applied ONCE
 * here: on every point of a row entry (`OptomechMaterialPropertiesSchema`), on a
 * datasheet answer and on an inline material in datasheet units. A row the
 * library accepts is a row a run can expand.
 */
export const OPTOMECH_PROPERTY_VALUE = {
  youngs_modulus_gpa: z.number().positive(),
  /** Below the incompressible limit: the bulk modulus E/(3(1 − 2ν)) is singular at ν = 0.5. */
  poisson_ratio: z.number().min(0).lt(0.5),
  density_g_cm3: z.number().positive(),
  /** Unbounded in sign on purpose: ULE and Zerodur sit at or below zero (OptomechMaterialSchema.cte_per_k). */
  cte_mean_ppm_per_k: z.number(),
  linear_expansion_strain: z.number(),
  conductivity_w_mk: z.number().positive(),
  specific_heat_j_kgk: z.number().positive(),
  refractive_index: z.number().gt(1),
  dndt_abs_per_k: z.number(),
  /** Signed as the datasheet prints it: TIE-27 states K can be negative for some glass types. */
  stress_optical_k_mm2_per_n: z.number(),
  tensile_strength_mpa: z.number().positive(),
} as const;

/**
 * A glass's index and ABSOLUTE dn/dT (1/K) at one wavelength and temperature,
 * with their source (SigFit SOL DNDT, FEA spec 2026-09-24 §0.4): what a datasheet
 * states on one line, what a thermo-optic pass integrates through the lens, and
 * what a run records having used. A row keeps it as one point of each of
 * `refractive_index` and `dndt_abs_per_k` at the same wavelength and temperature
 * (`materialRowView` pairs them back); the solver wire takes it through
 * `thermoOpticForRun`.
 */
export const OptomechThermoOpticPointSchema = z.object({
  index: OPTOMECH_PROPERTY_VALUE.refractive_index,
  dndt_abs_per_k: OPTOMECH_PROPERTY_VALUE.dndt_abs_per_k,
  wavelength_um: z.number().positive(),
  t_c: z.number(),
  source: z.string().min(1),
});
export type OptomechThermoOpticPoint = z.infer<typeof OptomechThermoOpticPointSchema>;

/**
 * The stress optical coefficient K = K∥ − K⊥ in mm²/N at the wavelength and
 * temperature the datasheet states it (SCHOTT: 589.3 nm, 21 °C, TIE-27 §2.2),
 * with its source: what a mechanical case's stress birefringence map is K times
 * (FEA spec §4.8). A row keeps it as one point of `stress_optical_k_mm2_per_n`.
 */
export const OptomechStressOpticalPointSchema = z.object({
  k_mm2_per_n: OPTOMECH_PROPERTY_VALUE.stress_optical_k_mm2_per_n,
  wavelength_um: z.number().positive(),
  t_c: z.number(),
  source: z.string().min(1),
});
export type OptomechStressOpticalPoint = z.infer<typeof OptomechStressOpticalPointSchema>;

/**
 * A tensile strength in MPa with where it comes from and what it applies to
 * (the basis, the temper, the product form): what a stress report's margin is
 * screened against. Never estimated; a body without one gets no margin. The
 * solver wire takes it as `tensile_strength_mpa` + `strength_source`, the
 * condition folded into the source by `strengthSourceText`.
 */
export const SourcedStrengthSchema = z.object({
  mpa: OPTOMECH_PROPERTY_VALUE.tensile_strength_mpa,
  source: z.string().min(1),
  condition: z.string().min(1).optional(),
});
export type SourcedStrength = z.infer<typeof SourcedStrengthSchema>;

/** The solver wire's thermo-optic record (fea/model.py ThermoOptic) from the point: the index is quoted at the point's temperature. */
export const thermoOpticForRun = (p: OptomechThermoOpticPoint): OptomechThermoOptic => ({
  index: p.index,
  dndt_per_k: p.dndt_abs_per_k,
  index_at_c: p.t_c,
  source: p.source,
});

/**
 * Why a thermo-optic point cannot go into a run at `runUm`, or null when it can.
 * The solver wire carries no wavelength on purpose: the index and dn/dT ARE at the
 * run's wavelength (fea/model.py ThermoOptic), so a point stated at another
 * wavelength is refused by name, never converted or sent as if it were the run's.
 * The tolerance is the catalog's rounding of a spectral line (1 nm).
 */
export function thermoOpticWavelengthMismatch(p: Pick<OptomechThermoOpticPoint, 'wavelength_um'>, runUm: number): string | null {
  if (Math.abs(p.wavelength_um - runUm) <= OPTOMECH_WAVELENGTH_TOL_UM) return null;
  return `Thermo-optic data is at ${p.wavelength_um} µm; the run is at ${runUm} µm. Enter index and dn/dT at ${runUm} µm.`;
}

/** The one string the solver wire's `strength_source` carries: the source, with the condition the figure applies to in brackets. */
export const strengthSourceText = (s: SourcedStrength): string => (s.condition ? `${s.source} (${s.condition})` : s.source);

/**
 * A material in datasheet units, as the agent tool, the run form and a library
 * row's expansion hold it: the one inline shape, projected to the solver
 * contract by `toContractMaterial`.
 */
export const OptomechDatasheetUnitsSchema = z.object({
  /** 1–80 of [A-Za-z0-9_-]: it becomes the CalculiX *MATERIAL name. */
  name: z.string().regex(OPTOMECH_DECK_NAME_RE),
  youngs_modulus_gpa: OPTOMECH_PROPERTY_VALUE.youngs_modulus_gpa,
  poisson_ratio: OPTOMECH_PROPERTY_VALUE.poisson_ratio,
  density_g_cm3: OPTOMECH_PROPERTY_VALUE.density_g_cm3,
  cte_ppm_per_k: OPTOMECH_PROPERTY_VALUE.cte_mean_ppm_per_k,
  /** Where the numbers came from (datasheet, catalog file). */
  source: z.string().min(1),
  /** W/(m·K): what a gradient over several bodies needs on each. */
  conductivity_w_mk: OPTOMECH_PROPERTY_VALUE.conductivity_w_mk.optional(),
  /** Glass only: the point a thermo-optic pass integrates with; the caller has checked it is at the run's wavelength (`thermoOpticWavelengthMismatch`). */
  thermo_optic: OptomechThermoOpticPointSchema.optional(),
  tensile_strength: SourcedStrengthSchema.optional(),
});
export type OptomechDatasheetUnits = z.infer<typeof OptomechDatasheetUnitsSchema>;

/**
 * Datasheet units → the solver contract's: E GPa → MPa, ρ g/cm³ → t/mm³, CTE
 * 10⁻⁶/K → 1/K, k W/(m·K) → W/(mm·K); every number to 12 significant digits
 * (sig12); the thermo-optic point and the strength projected to the wire's
 * spelling.
 */
export function toContractMaterial(m: OptomechDatasheetUnits): OptomechMaterial {
  return {
    name: m.name,
    youngs_modulus_mpa: sig12(m.youngs_modulus_gpa * 1000),
    poisson_ratio: sig12(m.poisson_ratio),
    density_t_per_mm3: sig12(m.density_g_cm3 / 1e9),
    cte_per_k: sig12(m.cte_ppm_per_k / 1e6),
    source: m.source,
    ...(m.conductivity_w_mk === undefined ? {} : { conductivity_w_per_mm_k: sig12(m.conductivity_w_mk / 1000) }),
    ...(m.thermo_optic ? { thermo_optic: thermoOpticForRun(m.thermo_optic) } : {}),
    ...(m.tensile_strength ? { tensile_strength_mpa: sig12(m.tensile_strength.mpa), strength_source: strengthSourceText(m.tensile_strength) } : {}),
  };
}

// --- the row's entries ------------------------------------------------------------

const entryMeta = {
  /** Where this entry's numbers come from (an OptomechCitation composed to text). Required on every entry: a row mixes sources by construction (spec §8.1). */
  source: z.string().min(1),
  /** What the value applies to: a temper, a heat treatment, a wavelength basis. */
  condition: z.string().min(1).optional(),
  /** The source's own stated uncertainty, in percent; recorded when stated, never invented. */
  uncertainty_pct: z.number().nonnegative().optional(),
};

const sortedUniqueTemperatures = (points: { t_c: number }[]) =>
  points.every((p, i) => i === 0 || p.t_c > points[i - 1].t_c);

/**
 * A property against temperature. ONE point is a constant: the datasheet's
 * room-temperature value, which the source states as the value and which a
 * run uses at any reference temperature. TWO OR MORE points are a table:
 * linear interpolation between them, and no extrapolation outside them (the
 * run is refused naming the material, the property and the covered range).
 * `pointsOf(value)` is the same entry with the property's bound on each value.
 */
const pointsOf = <V extends z.ZodNumber>(value: V) => z.object({
  points: z.array(z.object({ t_c: z.number(), value })).min(1).refine(sortedUniqueTemperatures, { message: 'points must be sorted by t_c, unique in t_c' }),
  ...entryMeta,
});
export const OptomechMaterialPointsSchema = pointsOf(z.number());
export type OptomechMaterialPoints = z.infer<typeof OptomechMaterialPointsSchema>;

/**
 * A datasheet's mean expansion coefficient over a stated interval (Schott's
 * "−30/+70 °C", a producer's "20–100 °C"): a secant over THAT interval, usable
 * for a run whose own interval lies inside it (spec §4.1.2).
 */
export const OptomechMaterialCteIntervalsSchema = z.object({
  intervals: z.array(z.object({ t0_c: z.number(), t1_c: z.number(), value: OPTOMECH_PROPERTY_VALUE.cte_mean_ppm_per_k }).refine((i) => i.t1_c > i.t0_c, { message: 't1_c must exceed t0_c' })).min(1),
  ...entryMeta,
});
export type OptomechMaterialCteIntervals = z.infer<typeof OptomechMaterialCteIntervalsSchema>;

/**
 * Expansion as the strain itself, (L − L_ref)/L_ref against temperature, from
 * a stated reference temperature (NIST publishes this form, relative to 293 K).
 * It gives the exact secant for any interval inside its range, whatever the
 * run's reference temperature. At least two points: one strain is no curve.
 */
export const OptomechMaterialStrainSchema = z.object({
  reference_t_c: z.number(),
  points: z.array(z.object({ t_c: z.number(), value: OPTOMECH_PROPERTY_VALUE.linear_expansion_strain })).min(2).refine(sortedUniqueTemperatures, { message: 'points must be sorted by t_c, unique in t_c' }),
  ...entryMeta,
});
export type OptomechMaterialStrain = z.infer<typeof OptomechMaterialStrainSchema>;

/**
 * Glass only: ABSOLUTE dn/dT (1/K) by temperature and wavelength (SigFit SOL
 * DNDT, spec 2026-09-24 §0.4), and the refractive index the same way. The two
 * entries come together and point for point — `materialRowView.thermoOpticPoints`
 * pairs them into OptomechThermoOpticPoints. A run reads them at ITS wavelength:
 * among the points within OPTOMECH_WAVELENGTH_TOL_UM of it, ONE is the datasheet
 * value and is used as is, recording its own temperature beside the run's
 * reference temperature; none means the row carries no thermo-optic data for that
 * wavelength and the lens is named without it; several are refused by name until
 * an interpolation rule is written (fail closed, rule 2 — the same licence as K,
 * FEA spec §4.8).
 */
const byWavelength = <V extends z.ZodNumber>(value: V) => z.object({
  points: z.array(z.object({ t_c: z.number(), wavelength_um: z.number().positive(), value })).min(1),
  ...entryMeta,
});
export const OptomechMaterialDndtSchema = byWavelength(z.number());
export type OptomechMaterialDndt = z.infer<typeof OptomechMaterialDndtSchema>;

/**
 * Glass only: the stress optical coefficient K = K∥ − K⊥ in mm²/N, by temperature
 * and wavelength, as the datasheet prints it (SCHOTT measures K at 589.3 nm and
 * 21 °C, TIE-27 July 2004 §2.2; N-BK7 K = 2.76·10⁻⁶ mm²/N, SCHOTT datasheet
 * collection 2022-02-01 p. 13). What a mechanical case's stress birefringence
 * map is K times (FEA spec 2026-09-24 §4.8, rule 11). ONE point is the constant
 * the datasheet states and a run uses it as is, recording its own wavelength and
 * reference temperature beside it; a row with SEVERAL points is refused by name
 * until an interpolation rule is written in §4.8 (fail closed, rule 2).
 */
export const OptomechMaterialStressOpticalSchema = byWavelength(z.number());
export type OptomechMaterialStressOptical = z.infer<typeof OptomechMaterialStressOpticalSchema>;

const uniqueSources = (sources: string[]) => sources.filter((s, i, a) => a.indexOf(s) === i).join('; ');

/**
 * The row's thermo-optic points: `refractive_index` and `dndt_abs_per_k` paired
 * one to one by wavelength (within OPTOMECH_WAVELENGTH_TOL_UM) at the same
 * temperature, each pair one OptomechThermoOpticPoint whose source names both
 * entries'. A row carries both entries or neither, and every point of one has
 * its partner in the other: one without the other is a half-filled row — the
 * properties schema refuses it at write, and a run refuses it by name whatever
 * the load case (rule 2), never a lens quietly without data.
 */
export function thermoOpticPointsOf(p: Pick<OptomechMaterialProperties, 'refractive_index' | 'dndt_abs_per_k'>): { points: OptomechThermoOpticPoint[] } | { error: string } {
  const index = p.refractive_index;
  const dndt = p.dndt_abs_per_k;
  if (!index && !dndt) return { points: [] };
  if (!index || !dndt) {
    const [has, lacks] = index ? ['refractive_index', 'dndt_abs_per_k'] : ['dndt_abs_per_k', 'refractive_index'];
    return { error: `carries ${has} without ${lacks}; a thermo-optic pass needs both (add the other with its source, or remove this one)` };
  }
  const source = uniqueSources([index.source, dndt.source]);
  // A complete pairing, not the first partner in sight: with one symmetric tolerance on a line, walking both
  // sides in (temperature, wavelength) order and pairing the two fronts whenever they are within it finds a
  // perfect matching whenever one exists (a front left behind has no partner at all). The points are emitted
  // in the index entry's own order.
  const byTandWavelength = (a: { t_c: number; wavelength_um: number }, b: { t_c: number; wavelength_um: number }) => a.t_c - b.t_c || a.wavelength_um - b.wavelength_um;
  const ns = index.points.map((point, at) => ({ point, at })).sort((a, b) => byTandWavelength(a.point, b.point));
  const ds = [...dndt.points].sort(byTandWavelength);
  const unpairedIndex = (n: { wavelength_um: number; t_c: number }) => ({ error: `refractive_index has a point at ${n.wavelength_um} µm, ${n.t_c} °C and dndt_abs_per_k has none within ${OPTOMECH_WAVELENGTH_TOL_UM} µm at that temperature; the two come as pairs` });
  const unpairedDndt = (d: { wavelength_um: number; t_c: number }) => ({ error: `dndt_abs_per_k has a point at ${d.wavelength_um} µm, ${d.t_c} °C and refractive_index has none within ${OPTOMECH_WAVELENGTH_TOL_UM} µm at that temperature; the two come as pairs` });
  const paired: OptomechThermoOpticPoint[] = [];
  let i = 0;
  let j = 0;
  while (i < ns.length && j < ds.length) {
    const n = ns[i].point;
    const d = ds[j];
    if (n.t_c === d.t_c && Math.abs(d.wavelength_um - n.wavelength_um) <= OPTOMECH_WAVELENGTH_TOL_UM) {
      paired[ns[i].at] = { index: n.value, dndt_abs_per_k: d.value, wavelength_um: n.wavelength_um, t_c: n.t_c, source };
      i += 1;
      j += 1;
    } else if (byTandWavelength(n, d) < 0) {
      return unpairedIndex(n);
    } else {
      return unpairedDndt(d);
    }
  }
  if (i < ns.length) return unpairedIndex(ns[i].point);
  if (j < ds.length) return unpairedDndt(ds[j]);
  return { points: paired };
}

/**
 * The property entries a material row may carry. Every key is optional and an
 * absent property is ABSENT: expansion fails closed naming it (spec rule 1).
 * Strict: an unknown key is a typo the route rejects, not a silent extra.
 * Every point value carries its property's bound (OPTOMECH_PROPERTY_VALUE), and
 * the thermo-optic entries come as pairs (thermoOpticPointsOf).
 */
export const OptomechMaterialPropertiesSchema = z.object({
  youngs_modulus_gpa: pointsOf(OPTOMECH_PROPERTY_VALUE.youngs_modulus_gpa).optional(),
  poisson_ratio: pointsOf(OPTOMECH_PROPERTY_VALUE.poisson_ratio).optional(),
  density_g_cm3: pointsOf(OPTOMECH_PROPERTY_VALUE.density_g_cm3).optional(),
  cte_mean_ppm_per_k: OptomechMaterialCteIntervalsSchema.optional(),
  linear_expansion_strain: OptomechMaterialStrainSchema.optional(),
  conductivity_w_mk: pointsOf(OPTOMECH_PROPERTY_VALUE.conductivity_w_mk).optional(),
  specific_heat_j_kgk: pointsOf(OPTOMECH_PROPERTY_VALUE.specific_heat_j_kgk).optional(),
  dndt_abs_per_k: byWavelength(OPTOMECH_PROPERTY_VALUE.dndt_abs_per_k).optional(),
  /** Glass only: n by temperature and wavelength, point for point with dndt_abs_per_k; with it the thermo-optic pass's input. */
  refractive_index: byWavelength(OPTOMECH_PROPERTY_VALUE.refractive_index).optional(),
  stress_optical_k_mm2_per_n: byWavelength(OPTOMECH_PROPERTY_VALUE.stress_optical_k_mm2_per_n).optional(),
  /**
   * A tensile strength in MPa with its source: what a stress report's margin is screened against
   * (FEA spec §4.3 stress report, 2026-10-27). ONE point is the figure the source states, and a run
   * uses it as is at any temperature: a metal's handbook design allowable (MIL-HDBK-5J Ftu, with
   * the basis and the product form in `condition`) or a glass datasheet figure. Never estimated;
   * absent when the catalog has none, and then no margin is reported for the body.
   */
  tensile_strength_mpa: pointsOf(OPTOMECH_PROPERTY_VALUE.tensile_strength_mpa).optional(),
}).strict().superRefine((p, ctx) => {
  const thermo = thermoOpticPointsOf(p);
  if ('error' in thermo) ctx.addIssue({ code: 'custom', message: thermo.error, path: [p.refractive_index ? 'refractive_index' : 'dndt_abs_per_k'] });
});
export type OptomechMaterialProperties = z.infer<typeof OptomechMaterialPropertiesSchema>;

/** The properties kept as points against temperature alone: what `materialRowView.at` reads. */
export const OPTOMECH_POINTS_PROPERTIES = ['youngs_modulus_gpa', 'poisson_ratio', 'density_g_cm3', 'conductivity_w_mk', 'specific_heat_j_kgk', 'tensile_strength_mpa'] as const;
export type OptomechPointsProperty = typeof OPTOMECH_POINTS_PROPERTIES[number];

// --- reading a row --------------------------------------------------------------

/**
 * A points entry read: the constant a one-point entry states at any
 * temperature; a table's linear interpolation at the temperature given, nothing
 * outside its range (the range named; a table is never extrapolated), and no
 * one value at all when no temperature is given (`tabulated`, the range named).
 */
export type OptomechPointsReading =
  | { kind: 'constant'; value: number }
  | { kind: 'interpolated'; value: number; range_c: [number, number] }
  | { kind: 'outside'; range_c: [number, number] }
  | { kind: 'tabulated'; range_c: [number, number] };

export function readPointsAt(entry: Pick<OptomechMaterialPoints, 'points'>, tC?: number): OptomechPointsReading {
  const { points } = entry;
  if (points.length === 1) return { kind: 'constant', value: points[0].value };
  const range_c: [number, number] = [points[0].t_c, points[points.length - 1].t_c];
  if (tC === undefined) return { kind: 'tabulated', range_c };
  if (tC < range_c[0] || tC > range_c[1]) return { kind: 'outside', range_c };
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (tC <= b.t_c) return { kind: 'interpolated', value: a.value + ((b.value - a.value) * (tC - a.t_c)) / (b.t_c - a.t_c), range_c };
  }
  return { kind: 'interpolated', value: points[points.length - 1].value, range_c };
}

/** The row's thermo-optic data at one wavelength: the one point within OPTOMECH_WAVELENGTH_TOL_UM, none, several (refused by name until an interpolation rule exists), or a row whose two entries do not pair. */
export type OptomechThermoOpticReading =
  | { kind: 'point'; point: OptomechThermoOpticPoint }
  | { kind: 'none' }
  | { kind: 'several'; points: OptomechThermoOpticPoint[] }
  | { kind: 'malformed'; error: string };

/** The row's stress optical coefficient: the one datasheet point a run uses as is, or several (refused by name, FEA spec §4.8), or none. */
export type OptomechStressOpticalReading =
  | { kind: 'point'; point: OptomechStressOpticalPoint }
  | { kind: 'several'; points: OptomechStressOpticalPoint[] }
  | { kind: 'none' };

/**
 * What one number a row's entry gives — the row's own operation, read the same
 * way by the backend's expansion, the agent's summary and the run form, so a
 * tabulated E (every NIST-fit metal seed) is a value at the reference
 * temperature everywhere, not blank in one place and expanded in another.
 */
export interface OptomechMaterialRowView {
  /** The entry's reading at `tC`, or without a temperature the one figure it states (a table then has none); null when the row lacks the property. */
  at(property: OptomechPointsProperty, tC?: number): OptomechPointsReading | null;
  /** The strength at `tC` (or the one figure stated, without a temperature) with its own source and condition; null when the row carries none or the reading gives no value. */
  strength(tC?: number): SourcedStrength | null;
  /** Every thermo-optic point the row carries (the two entries paired); an error for a row whose entries do not pair. */
  thermoOpticPoints(): { points: OptomechThermoOpticPoint[] } | { error: string };
  thermoOpticAt(wavelengthUm: number): OptomechThermoOpticReading;
  stressOptical(): OptomechStressOpticalReading;
}

export function materialRowView(row: Pick<OptomechMaterialRow, 'properties'>): OptomechMaterialRowView {
  const p = row.properties;
  const thermo = thermoOpticPointsOf(p);
  return {
    at: (property, tC) => {
      const entry = p[property];
      return entry ? readPointsAt(entry, tC) : null;
    },
    strength: (tC) => {
      const entry = p.tensile_strength_mpa;
      if (!entry) return null;
      const reading = readPointsAt(entry, tC);
      if (!('value' in reading)) return null;
      return { mpa: reading.value, source: entry.source, ...(entry.condition ? { condition: entry.condition } : {}) };
    },
    thermoOpticPoints: () => thermo,
    thermoOpticAt: (wavelengthUm) => {
      if ('error' in thermo) return { kind: 'malformed', error: thermo.error };
      const near = thermo.points.filter((pt) => Math.abs(pt.wavelength_um - wavelengthUm) <= OPTOMECH_WAVELENGTH_TOL_UM);
      if (near.length === 0) return { kind: 'none' };
      if (near.length > 1) return { kind: 'several', points: near };
      return { kind: 'point', point: near[0] };
    },
    stressOptical: () => {
      const entry = p.stress_optical_k_mm2_per_n;
      if (!entry) return { kind: 'none' };
      const points = entry.points.map((pt): OptomechStressOpticalPoint => ({ k_mm2_per_n: pt.value, wavelength_um: pt.wavelength_um, t_c: pt.t_c, source: entry.source }));
      return points.length === 1 ? { kind: 'point', point: points[0] } : { kind: 'several', points };
    },
  };
}

// --- the row ----------------------------------------------------------------------

/** One material VERSION as the API serves it (the current one unless a versions listing is asked for). */
export const OptomechMaterialRowSchema = z.object({
  /** The version id: what a run's materials_used names. */
  id: z.string().uuid(),
  /** Stable across versions: what a binding and a run request name. */
  material_id: z.string().uuid(),
  version: z.number().int().positive(),
  scope: OptomechMaterialScopeSchema,
  project_id: z.string().uuid().nullable(),
  kind: OptomechMaterialKindSchema,
  name: z.string(),
  deck_name: z.string().regex(OPTOMECH_DECK_NAME_RE),
  catalog_glass: z.string().nullable(),
  properties: OptomechMaterialPropertiesSchema,
  source: z.string(),
  notes: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  superseded_at: z.string().nullable(),
});
export type OptomechMaterialRow = z.infer<typeof OptomechMaterialRowSchema>;

export const OptomechMaterialListSchema = z.object({ materials: z.array(OptomechMaterialRowSchema) });
export const OptomechMaterialVersionListSchema = z.object({ versions: z.array(OptomechMaterialRowSchema) });

/**
 * Body of POST /optomech/materials (a new project-scoped material) and of
 * POST /optomech/materials/{materialId}/versions (a new version of one the
 * project owns). Global rows are written from the admin area (P2), never here.
 * Every write path builds its row through the backend's one `materialVersion`
 * constructor, which derives `deck_name` from `name` when none is given.
 */
export const OptomechMaterialCreateSchema = z.object({
  project_id: z.string().uuid(),
  kind: OptomechMaterialKindSchema,
  name: z.string().min(1).max(120),
  /** The deck spelling when it is not `deckNameOf(name)` (a seed's "Al6061-T6" for "Aluminum 6061-T6"); derived from the name when absent. */
  deck_name: z.string().regex(OPTOMECH_DECK_NAME_RE).optional(),
  catalog_glass: z.string().min(1).optional(),
  properties: OptomechMaterialPropertiesSchema,
  source: z.string().min(1),
  notes: z.string().min(1).optional(),
});
export type OptomechMaterialCreate = z.infer<typeof OptomechMaterialCreateSchema>;

/**
 * The four datasheet numbers with what makes them a sourced row (spec §6.3):
 * the temperature E, ν and ρ are quoted at, the interval the mean CTE is
 * stated over, and the source. What a user's answer — in chat or in the run
 * form — is kept as, through `propertiesFromDatasheet`.
 */
export const OptomechDatasheetMaterialSchema = z.object({
  kind: OptomechMaterialKindSchema,
  name: z.string().min(1).max(120),
  youngs_modulus_gpa: OPTOMECH_PROPERTY_VALUE.youngs_modulus_gpa,
  poisson_ratio: OPTOMECH_PROPERTY_VALUE.poisson_ratio,
  density_g_cm3: OPTOMECH_PROPERTY_VALUE.density_g_cm3,
  /** The datasheet's MEAN coefficient over `cte_interval_c`, 10⁻⁶/K. */
  cte_ppm_per_k: OPTOMECH_PROPERTY_VALUE.cte_mean_ppm_per_k,
  cte_interval_c: z.tuple([z.number(), z.number()]).refine(([a, b]) => b > a, { message: 'the CTE interval must run from a lower to a higher temperature' }),
  /** The temperature E, ν and ρ are quoted at, °C. */
  quoted_at_c: z.number(),
  source: z.string().min(1),
  condition: z.string().min(1).optional(),
  catalog_glass: z.string().min(1).optional(),
  /**
   * Glass only: the datasheet's stress optical coefficient K, kept as ONE point of
   * `properties.stress_optical_k_mm2_per_n` with its own source. Omitted, the row
   * carries no K and a mechanical run reports no birefringence map for the body,
   * naming it (FEA spec §4.8).
   */
  stress_optical: OptomechStressOpticalPointSchema.optional(),
  /**
   * Glass only: the datasheet's index and ABSOLUTE dn/dT at one wavelength and
   * temperature, kept as one point each of `properties.refractive_index` and
   * `properties.dndt_abs_per_k` with their own source (SCHOTT publishes dn/dT_abs
   * per wavelength and 20 °C interval). Omitted, the row carries no thermo-optic
   * data and a thermal run reports no OPD map for the body, naming it.
   */
  thermo_optic: OptomechThermoOpticPointSchema.optional(),
  /** The datasheet's thermal conductivity in W/(m·K) at `quoted_at_c`, kept as one point of `properties.conductivity_w_mk`; what a gradient over several bodies needs on each. */
  conductivity_w_mk: OPTOMECH_PROPERTY_VALUE.conductivity_w_mk.optional(),
  /** The source's tensile strength, kept as one point of `properties.tensile_strength_mpa` with its own source and condition. Omitted, the row carries no strength and a run reports no margin for the body. */
  tensile_strength: SourcedStrengthSchema.optional(),
});
export type OptomechDatasheetMaterial = z.infer<typeof OptomechDatasheetMaterialSchema>;

/**
 * Who or what wrote a binding. Resolution writes 'library_match' only; 'user',
 * 'agent' and 'step_declared_confirmed' are human-confirmed and sticky (spec
 * 2026-10-16 §4.3). 'catalog' is written by the composition build for a
 * catalog part's own body (spec 2026-10-19 §7): human-free and NOT sticky,
 * since the catalog is the authority for its own parts and a person may rebind.
 */
export const OPTOMECH_BINDING_HOW = ['library_match', 'user', 'agent', 'step_declared_confirmed', 'catalog'] as const;
export const OptomechBindingHowSchema = z.enum(OPTOMECH_BINDING_HOW);
export type OptomechBindingHow = z.infer<typeof OptomechBindingHowSchema>;

/** The evidence behind a binding (spec §5.3). */
export const OPTOMECH_BINDING_CONFIDENCE = ['exact', 'fingerprint', 'name', 'declared'] as const;
export const OptomechBindingConfidenceSchema = z.enum(OPTOMECH_BINDING_CONFIDENCE);

export const OptomechBodyBindingSchema = z.object({
  retained_geometry_ref: z.string(),
  occurrence_path: z.string(),
  material_id: z.string().uuid(),
  how: OptomechBindingHowSchema,
  confidence: OptomechBindingConfidenceSchema,
  evidence: z.record(z.string(), z.unknown()),
  bound_by: z.string(),
  bound_at: z.string(),
});
export type OptomechBodyBinding = z.infer<typeof OptomechBodyBindingSchema>;

/**
 * Body of PUT /optomech/bodies/binding: the user's answer for one body of the
 * conversation's retained file — an existing material by its stable id, or the
 * datasheet numbers to keep as a new project material and bind in one step.
 */
export const OptomechBodyBindingUpsertSchema = z.union([
  z.object({
    conversation_id: z.string().uuid(),
    occurrence_path: z.string().min(1),
    /** The stable id of a material the conversation's project can read (global, or its own). */
    material_id: z.string().uuid(),
  }),
  z.object({
    conversation_id: z.string().uuid(),
    occurrence_path: z.string().min(1),
    material: OptomechDatasheetMaterialSchema,
  }),
]);
export type OptomechBodyBindingUpsert = z.infer<typeof OptomechBodyBindingUpsertSchema>;

/**
 * GET /optomech/bodies — every meshable body of the conversation's retained
 * CAD file (the import's elements as lenses, its `mount` solids), joined with
 * its binding and the binding's current material (spec §4.4: a join at read
 * time, never a field on the system JSON). `retained_geometry_ref` is null and
 * `bodies` empty for a conversation without a retained import.
 */
export const OptomechBodySchema = z.object({
  occurrence_path: z.string(),
  name: z.string(),
  kind: z.enum(['lens', 'mount']),
  /** The system surfaces a lens owns (empty for a mount). */
  surfaces: z.array(z.number().int()),
  /**
   * The element a lens body belongs to: a cemented element's bodies (one per glass) share it, and each is bound
   * to its own material. The import's element id for an imported lens; null for a mount.
   */
  element_id: z.string().nullable(),
  /** The lens's glass on its first surface, when the system names one. */
  glass: z.string().nullable(),
  /**
   * The STEP file's own material attribute on this body (spec 2026-10-16 §5.4): a HINT the agent
   * offers for confirmation, never a property set. Null when the file declares none (the ordinary
   * case) and on imports saved before the importer reported it.
   */
  declared_material: CadDeclaredMaterialSchema.nullable(),
  binding: z.object({
    how: OptomechBindingHowSchema,
    confidence: OptomechBindingConfidenceSchema,
    bound_by: z.string(),
    bound_at: z.string(),
    /** The bound material's CURRENT version, or null when the project can no longer read it. */
    material: OptomechMaterialRowSchema.nullable(),
  }).nullable(),
});
export type OptomechBody = z.infer<typeof OptomechBodySchema>;

/**
 * GET /optomech/bodies: the design's optomech context as the backend reads it from the saved system, in one
 * response so its parts cannot come from two reads that disagree: the retained file and its meshable bodies
 * (each with its binding), the composition whose build wrote the system, and the run's wavelength.
 */
export const OptomechBodyListSchema = z.object({
  retained_geometry_ref: z.string().nullable(),
  bodies: z.array(OptomechBodySchema),
  /** The composition whose build wrote the saved system (`provenance.optomech_composition.model_id`); null for an import or no file. */
  composition_id: z.string().uuid().nullable(),
  /** The run's wavelength when a request names none: the system's primary, else its first; null when the conversation has no system. */
  primary_wavelength_um: z.number().positive().nullable(),
});
export type OptomechBodyList = z.infer<typeof OptomechBodyListSchema>;

/** An optomech context with nothing in it: a draft conversation, or one with no system yet. */
export const EMPTY_OPTOMECH_BODY_LIST: OptomechBodyList = { retained_geometry_ref: null, bodies: [], composition_id: null, primary_wavelength_um: null };

/**
 * Per body, what a run expanded (optomech_runs.materials_used, spec §4.5): the
 * material VERSION with the interval its CTE was the secant over, or the inline
 * numbers' source for a body the request carried numbers for.
 */
export const OptomechMaterialUsedSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('library'),
    /** The single-element run's lens, or the assembly body. */
    occurrence_path: z.string().nullable(),
    material_version_id: z.string().uuid(),
    material_id: z.string().uuid(),
    version: z.number().int(),
    name: z.string(),
    /** [t0, t1] the CTE was taken over; equal for the degenerate interval of a case with no temperature change. */
    cte_interval_c: z.tuple([z.number(), z.number()]),
    /** The composed per-property source string that went to fea-service as material.source. */
    source: z.string(),
    /**
     * The stress optical coefficient forwarded as the fea-service material's
     * `stress_optical_k_mm2_per_n` (FEA spec §4.8): the datasheet point used as
     * is, and the run's wavelength and reference temperature it was used at.
     * Absent when the row carries no K.
     */
    stress_optical_k: z.object({
      k_mm2_per_n: z.number(),
      at_wavelength_um: z.number(),
      at_t_c: z.number(),
      source: z.string(),
      run_wavelength_um: z.number(),
      run_reference_temperature_c: z.number(),
    }).optional(),
    /**
     * The thermo-optic point forwarded as the fea-service material's `thermo_optic`
     * (through `thermoOpticForRun`): the row's point at the run's wavelength, used
     * as is, and the run's wavelength it was read at. Absent when the row carries
     * none there.
     *
     * `point` is the record; the flat keys beside it are its earlier spelling,
     * deprecated and still written until the removal PR. `point` is optional
     * only because runs recorded before it was written carry the flat keys alone;
     * the removal PR decides whether to fold those into `point` (a migration) or
     * leave the old runs with no thermo-optic record.
     */
    thermo_optic: z.object({
      point: OptomechThermoOpticPointSchema.optional(),
      run_wavelength_um: z.number(),
      index: z.number().meta({ deprecated: true }),
      index_at_c: z.number().meta({ deprecated: true }),
      dndt_per_k: z.number().meta({ deprecated: true }),
      dndt_at_c: z.number().meta({ deprecated: true }),
      at_wavelength_um: z.number().meta({ deprecated: true }),
      source: z.string().meta({ deprecated: true }),
    }).optional(),
    /** The conductivity forwarded, W/(mm·K), read at the reference temperature. Absent when the row carries none. */
    conductivity_w_per_mm_k: z.number().optional(),
    /**
     * The strength forwarded as the fea-service material's `tensile_strength_mpa` and `strength_source`
     * (`strengthSourceText`): the row's point with its own source and condition. Absent when the row carries none.
     */
    tensile_strength: SourcedStrengthSchema.optional(),
  }),
  z.object({
    kind: z.literal('inline'),
    occurrence_path: z.string().nullable(),
    name: z.string(),
    source: z.string(),
  }),
]);
export type OptomechMaterialUsed = z.infer<typeof OptomechMaterialUsedSchema>;
